<template>
  <div>
    <div :class="`${need_show_tutorial ? 'overlay-blur' : ''}`"></div>
    <div class="z-card mb-4">
      <h2>{{ $t('autoinformer.campaign.form.title') }}</h2>

      <div class="z-card__body">
        <div
          :class="`${tutorial_steps[counter].id === 'name' ? `hint hint--${counter}` : ''}`"
          :title="tutorial_steps[counter].text"
        >
          <z-form-group class="row">
            <div class="col-3 text-right">
              {{ $t('autoinformer.campaign.field.name') }}
            </div>
            <z-form-input class="col-9" :rules="{ required: true }">
              <i-input v-model="form.name" />
            </z-form-input>
          </z-form-group>
        </div>

        <div
          :class="`${tutorial_steps[counter].id === 'caller_id' ? `hint hint--${counter}` : ''}`"
          :title="tutorial_steps[counter].text"
        >
          <z-form-group class="row">
            <div class="col-3 text-right">
              {{ $t('autoinformer.campaign.field.caller_id') }}
            </div>

            <z-form-input class="col-8" :rules="{ required: true }">
              <select-caller-id
                v-model="form.callers"
                @input="selectCallers"
                @set_data="set_data"
              />
            </z-form-input>
            <tooltip
              class="ml-3"
              placement="top"
              min-width="200"
              max-width="200"
              theme="light"
              :transfer="true"
            >
              <template #content>
                {{ $t('autocaller_v2.campaign.tooltips.caller_id') }} {{ ' ' }}
                <router-link :to="{ name: 'phone-number.number.buy' }">
                  {{ $t('phone-number.breadcrumb.title') }}
                </router-link>
              </template>
              <icon size="18" type="ios-help-circle-outline" />
            </tooltip>
          </z-form-group>
        </div>

        <div
          :class="`${tutorial_steps[counter].id === 'scheme' ? `hint hint--${counter}` : ''}`"
          :title="tutorial_steps[counter].text"
        >
          <z-form-group class="row">
            <div class="col-3 text-right">
              {{ $t('autoinformer.campaign.form.calling_scheme') }}
            </div>

            <template v-if="is_scheme_exist">
              <div class="col-9 flex">
                <i-button class="mr-4" type="default" size="large" @click="edit_scheme">
                  {{ $t('shared.component.table.action.edit') }}
                </i-button>
                <i-button type="default" size="large" @click="delete_scheme">
                  {{ $t('shared.component.table.action.delete') }}
                </i-button>
              </div>
            </template>
            <template v-else>
              <z-form-button class="col-9" :rules="{ required: true }">
                <i-button v-model="form.is_scheme" type="default" icon="md-add" long size="large" @click="create_scheme">
                  {{ $t('autoinformer.campaign.form.buttons.create_scheme') }}
                </i-button>
              </z-form-button>
            </template>
          </z-form-group>
        </div>
        <z-form-group class="row">
          <div class="col-3 text-right">
            {{ $t('autoinformer.campaign.field.attempts') }}
          </div>
          <z-form-input class="col-9">
            <input-number
              v-model="attempts_interval_min"
              :min="1"
              :max="12"
              @input="checkStrategy"
              @on-blur="attemptsIntervalMin"
            />
          </z-form-input>
        </z-form-group>
        <z-form-group v-if="attempts_interval_min >= 2" class="row">
          <z-form-input class="col-9 offset-3">
            <select-number-attemps
              :times="attempts_interval_min"
              :strategy="form.strategy"
              @input="addStrategy"
            />
          </z-form-input>
        </z-form-group>

        <z-form-group class="row">
          <div class="col-3 text-right">
            {{ $t('autoinformer.campaign.field.calls_limit') }}
          </div>
          <z-form-input class="col-9" :rules="{ min_value: 1, max_value: 30 }">
            <input-number v-model="form.call_limit" :min="1" :max="30" />
          </z-form-input>
        </z-form-group>

        <z-form-group class="row">
          <div class="col-3 text-right">
            {{ $t('autoinformer.campaign.field.dial_timeout') }}
          </div>
          <z-form-input class="col-9" :rules="{ min_value: 0, max_value: 60 }">
            <input-number v-model="form.dial_timeout" :min="0" :max="60" />
            <tooltip
              class="ml-3"
              placement="right"
              min-width="300"
              max-width="300"
              theme="light"
              :content="$t('autoinformer.campaign.tooltips.dial_timeout')"
            >
              <icon size="18" type="ios-help-circle-outline" />
            </tooltip>
          </z-form-input>
        </z-form-group>

        <z-form-group class="row">
          <div class="col-3 text-right">
            {{ $t('autoinformer.campaign.field.need_amd') }}
          </div>
          <z-form-input class="col-3 flex">
            <z-switch v-model="form.need_amd" />
            <tooltip
              class="ml-3"
              placement="right"
              min-width="300"
              max-width="300"
              theme="light"
              :transfer="true"
              :content="$t('autoinformer.campaign.tooltips.need_amd')"
            >
              <icon size="18" type="ios-help-circle-outline" />
            </tooltip>
          </z-form-input>
        </z-form-group>
      </div>

      <h2>{{ $t('autoinformer.campaign.form.title_schedule') }}</h2>
      <div class="z-card__body">
        <z-form-group class="row">
          <label class="col-2 text-right">
            {{ $t('shared.component.table.field.time_zone') }}
          </label>

          <div class="col-10">
            <z-form-input>
              <time-zone-select v-model="form.timezone" />
            </z-form-input>
          </div>
        </z-form-group>

        <z-form-group v-if="form.modify_work_time.length" class="row">
          <div class="offset-2 col-10">
            <work-time-form v-model="form.modify_work_time" :with_id="false" />
          </div>
        </z-form-group>

        <z-form-group class="row">
          <div class="offset-2 col-10">
            <i-button type="default" icon="md-add" long size="large" @click="create_time_interval">
              {{ $t('pbx.time-condition.form.buttons.create_time_interval') }}
            </i-button>
          </div>
        </z-form-group>
      </div>

      <div v-if="need_show_tutorial" :class="`${need_show_tutorial ? 'tutorial-form' : ''}`">
        <div
          class="tutorial-navigation-wrap tutorial-navigation-wrap--close"
          @click="close_tutorial"
        ></div>
        <i-button
          v-if="need_show_tutorial && counter + 1 < tutorial_steps.length"
          class="tutorial-navigation-wrap--pagination-button"
          type="default"
          size="medium"
          @click="forward_tutorial"
        >
          {{ $t('shared.component.button.next') }}
        </i-button>
        <div class="tutorial-navigation-wrap tutorial-navigation-wrap--pagination">
          <div v-if="counter !== 0" class="arrow--back" @click="backward_tutorial"></div>
          {{ counter + 1 }} / {{ tutorial_steps.length }}

          <div
            v-if="counter + 1 < tutorial_steps.length"
            class="arrow"
            @click="forward_tutorial"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import SelectCallerId from '@autoinformer/components/campaigns-input-caller-id';
  import { TimeConditionModalService } from '@pbx/modals/TimeConditionModalService';
  import { CampaignsModalService } from '@autoinformer/modals/CampaignsModalService';
  import cloneDeep from 'lodash-es/cloneDeep';
  import TimeZoneSelect from '@shared/components/input/input-timezone-select';
  import WorkTimeForm from '@pbx/components/time-conditions/form/_fragments/worktime-form';
  import SelectNumberAttemps from '@autocaller_v2/components/campaigns-input-number-attempts';
  import { CampaignsService } from '@autoinformer/services/CampaignsService';
  import isArraysEqual from '@utils/isArraysEqual';

  const initial_form = {
    name: '',
    callers: [],
    call_limit: 1,
    timezone: null,
    strategy: [],
    modify_work_time: [],
    dial_timeout: 30,
    need_amd: false,
    is_scheme: false 
  };

  export default {
    components: { SelectCallerId, SelectNumberAttemps, TimeZoneSelect, WorkTimeForm },
    props: {
      campaign: {
        type: [Object, Boolean],
        default: false
      },
      counter: {
        type: Number,
        default: 0
      },
      tutorial_steps: {
        type: Array,
        default: () => []
      },
      need_show_tutorial: {
        type: Boolean,
        default: false
      },
    },
    data: function () {
      return {
        is_edit: false,
        is_scheme_exist: false,
        is_loading: false,
        attempts_interval_min: 1,
        scheme_type: 'media',
        form: initial_form,
        initial_form: initial_form,
        numbers: [],
        need_amd: false,
        empty_media_file: false,
      };
    },
    computed: {},
    watch: {
      form: {
        immediate: true,
        handler(value) {
          this.$emit('input', value);
        }
      },
      empty_media_file: {
        immediate: true,
        handler(value) {
          this.$emit('input_empty_media_file', value);
        }
      },
      empty_scheme: {
        immediate: true,
        handler(value) {
          this.$emit('input_empty_scheme', value);
        }
      },

    },

    mounted() {
      window.addEventListener('popstate', this.checkBrowserBackPress);
    },
    created() {
      this.form.timezone = this.$user.timezone;

      if (this.$router.history.current.path.includes('edit')) {
        this.is_edit = true;
      }

      CampaignsService.getScheme(this.campaign.id).then((responseSchemeList) => {
        if (responseSchemeList[0]?.id) {
          this.is_scheme_exist = true;
          this.is_scheme = true;
          this.$emit('input_empty_media_file', false);
          this.$emit('input_is_scheme_exist', true);

          const mediaBoxes = responseSchemeList?.filter(i => i.type === 'media' || i.type === 'menu' );
          const mediaBoxesWithSourceLength = responseSchemeList.filter(i => (i.data.params.media && i.type === 'media') || (i.data.params.media && i.type === 'menu')).length;
          
          if(mediaBoxes.length > mediaBoxesWithSourceLength) {
            this.$emit('input_empty_media_file', true);
            this.$noty.warning(this.$t('autoinformer.validator.empty_mediafiles'), {
              timeout: 5000,
              maxVisible : 1,
              layout: 'bottomCenter',
              animation: {
                open : 'animated fadeInUp',
                close : 'animated fadeOutDown',
              }
            });
          } else {
            this.$emit('input_empty_media_file', false);
          }
        } else {
          this.$emit('input_is_scheme_exist', false);
        }
      });

      const fromSchemePage = this.$route.params.from_scheme;
      if (this.campaign) {
        const {
          name,
          callers,
          call_limit,
          type,
          timezone,
          strategy,
          worktime,
          dial_timeout,
          need_amd
        } = this.campaign;
        this.form = {
          name,
          callers: callers ? callers : [],
          call_limit,
          type,
          timezone,
          strategy: strategy ? strategy : [],
          modify_work_time: worktime ? worktime : [],
          dial_timeout,
          need_amd
        };

        this.initial_form = this.form;

        this.attempts_interval_min =
          this.form.strategy.length > 0 ? this.form.strategy.length + 1 : 1;
      }

      if (fromSchemePage) {
        this.field_storage();
        return;
      }
    },
    methods: {
      set_data(data) {
        this.numbers = data;
        this.$emit('set_numbers', data);
        this.form.callers = this.getCallers(this.form.callers);
      },

      checkBrowserBackPress() {
        this.field_storage();
      },
      backward_tutorial() {
        this.$emit('backward');
      },
      forward_tutorial() {
        this.$emit('forward');
      },
      close_tutorial() {
        this.$emit('close');
      },
      delete_scheme() {
        const campaign_id = this.$route.params.id;

        CampaignsModalService.deleteScheme(campaign_id).on(
          'success',
          () => {
            this.is_scheme_exist = false;
            this.is_scheme = false;
            this.$emit('input_is_scheme_exist', false);
          }
        );
      },

      // заполнялть когда он вернулся именно из схемы, потому что это может быть редактирование
      field_storage() {
        this.form = JSON.parse(localStorage.getItem('autoinformer_form'));
      },

      // когда он идет создавать или редактировать схему
      save_in_storage() {
        localStorage.setItem('autoinformer_form', JSON.stringify(this.form));
      },

      create_scheme() {
        this.save_in_storage();

        CampaignsModalService.create({ scheme_type: this.scheme_type }).on('success', (data) => {
          CampaignsService.initScheme(this.campaign.id, data).then(() => {
            this.$router.push({
              name: 'autoinformer.worksheet.edit',
              params: { id: this.campaign.id, tutorial: this.need_show_tutorial }
            });
          });
        });
      },
      edit_scheme() {
        this.save_in_storage();

        this.$router.push({
          name: 'autoinformer.worksheet.edit',
          params: { tutorial: this.need_show_tutorial }
        });
      },
      create_time_interval: function () {
        TimeConditionModalService.create_time_interval().on(
          'success',
          (items) =>
            (this.form.modify_work_time = [...this.form.modify_work_time, ...cloneDeep(items)])
        );
      },
      addStrategy(val, i) {
        this.form.strategy[i - 1] = val;
      },
      checkStrategy(i) {
        if (!this.attempts_interval_min) {
          this.form.strategy = [];
          return false;
        }
        if (this.form.strategy.length > i - 1) {
          this.form.strategy.length = this.attempts_interval_min - 1;
          return false;
        }
        this.form.strategy[i - 2] = 1;
      },
      attemptsIntervalMin() {
        if (!this.attempts_interval_min) this.attempts_interval_min = 1;
      },

      getCallers: function (callers) {
        const numbersWithoutAllPoint = this.numbers
          ?.filter((item) => item.number !== 'all')
          ?.map((item) => item.number);

        if (!callers) {
          return [];
        }

        if (isArraysEqual(numbersWithoutAllPoint, callers)) {
          return ['all'];
        } else {
          return callers;
        }
      },

      selectCallers: function (val) {
        if (val[val.length - 1] === 'all') {
          this.form.callers = ['all'];
        } else {
          this.form.callers = val.filter((i) => i !== 'all');
        }
      }
    }
  };
</script>

<style lang="scss">
  .tutorial-form {
    .hint {
      &--0 {
        &:before {
          right: -95px;
          top: 40%;
        }
        &:after {
          right: -245px;
          top: 70%;
        }
      }
      &--1 {
        &:before {
          right: -92px;
          top: 45%;
        }
        &:after {
          right: -420px;
          top: 75%;
        }
      }
      &--2 {
        &:before {
          right: -75px;
          top: 45%;
        }
        &:after {
          right: -380px;
          top: 67%;
        }
      }

      &--2 {
        &:before {
          right: -90px;
        }
      }

      &--3 {
        width: 150px;
        z-index: 100;

        &:before {
          right: -55px;
          top: 20%;
        }
        &:after {
          top: 70%;
          right: -400px;
          width: 400px;
        }
      }
    }
  }
</style>
